<script setup lang="ts">
  import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'
  import CloseIcon from '@/shared/components/icons/CloseIcon.vue'
  import type { BaseModalProps, Emit } from './BaseModal.types'

  const emit = defineEmits<Emit>()
  const props = withDefaults(defineProps<BaseModalProps>(), {
    showCloseButton: true,
  })

  const modalRef = ref<HTMLDialogElement | null>(null)
  const modalContentRef = ref<HTMLDivElement | null>(null)
  const disableContentScroll = ref(false)
  const mediaQuery = window.matchMedia(`(max-width: 550px)`)
  const isMobile = ref(mediaQuery.matches)

  let startTouchY = 0
  let currentTouchY = 0
  let touchTranslateY = 0
  const isSwiping = ref(false)
  const verticalOffset = ref<number>(0)

  watch(
    () => props.isOpen,
    (isModalOpen) => {
      if (isModalOpen) {
        openModal()
      } else {
        closeModal()
      }
    }
  )

  const handleOutsideClick = (target: EventTarget | null) => {
    const clickedOutside = target === modalRef.value
    if (clickedOutside) {
      closeModal()
    }
  }

  const onModalClick = (event: MouseEvent) => {
    handleOutsideClick(event.target)
  }

  const disableBodyScrolling = (disable = true) => {
    document.body.classList.toggle('no-scroll', disable)
  }

  const openModal = () => {
    disableBodyScrolling()
    resetModalState()
    modalRef.value?.showModal()
    if (modalContentRef.value) {
      modalContentRef.value.scrollTo({ top: 0 })
    }
  }

  const closeModal = () => {
    disableBodyScrolling(false)
    modalRef.value?.close()
    emit('close')
  }

  const resetModalState = () => {
    startTouchY = 0
    currentTouchY = 0
    touchTranslateY = 0
    if (modalContentRef.value && isMobile?.value) {
      verticalOffset.value = 0
      isSwiping.value = false
    }
  }

  const onTouchStartHandler = (event: TouchEvent) => {
    if (!isMobile?.value) return

    startTouchY = event.touches[0].clientY
    touchTranslateY = 0
    if (modalContentRef.value) {
      isSwiping.value = false
    }
  }

  const onTouchMoveHandler = (event: TouchEvent) => {
    if (!isMobile?.value) return
    if (modalContentRef.value?.scrollTop !== 0) return

    currentTouchY = event.touches[0].clientY
    const distance = currentTouchY - startTouchY
    if (distance > 0 && modalContentRef.value) {
      disableContentScroll.value = true
      touchTranslateY = distance
      verticalOffset.value = touchTranslateY
    }
  }

  const onTouchEndHandler = () => {
    if (!isMobile?.value || !modalContentRef.value) return

    disableContentScroll.value = false
    const contentHeight = modalContentRef.value.clientHeight
    const swipeThreshold = contentHeight * 0.5
    if (touchTranslateY > swipeThreshold) {
      closeModal()
    } else {
      isSwiping.value = true
      verticalOffset.value = 0
    }
  }

  const computedTranslateStyle = computed(() => {
    const defaultTransformForDesktop = 'translate(-50%, -50%)'
    return isMobile?.value ? `translateY(${verticalOffset.value}px)` : defaultTransformForDesktop
  })

  const updateIsMobile = () => {
    isMobile.value = mediaQuery.matches
  }

  onMounted(() => {
    window.addEventListener('resize', updateIsMobile)

    if (props.isOpen) {
      openModal()
    }
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', updateIsMobile)
  })
</script>

<template>
  <dialog ref="modalRef" class="modal" @close="closeModal" @click="onModalClick">
    <div
      ref="modalContentRef"
      :class="[
        'modal__content',
        {
          'modal__content--animate': isSwiping,
          'no-scroll': disableContentScroll,
        },
      ]"
      :id="id"
      :style="{ transform: computedTranslateStyle }"
      @touchstart="onTouchStartHandler"
      @touchmove="onTouchMoveHandler"
      @touchend="onTouchEndHandler"
    >
      <div v-if="showCloseButton" class="modal__close-icon-wrapper">
        <CloseIcon class="modal__close-icon" @click="closeModal" />
      </div>
      <slot />
    </div>
  </dialog>
</template>

<style lang="scss" scoped>
  @use './BaseModal';
</style>

<style lang="scss">
  .no-scroll {
    overflow: hidden !important;
  }
</style>
